import React, { useState } from 'react'
import BeautifulLogin from './components/BeautifulLogin'
import HomePage from './components/HomePage'
import UniversalDashboard from './components/UniversalDashboard'

export interface User {
  id: string
  email: string
  name: string
  role: string
  isVerified: boolean
  kycStatus: string
}

export interface AuthResponse {
  access_token: string
  user: User
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    console.error('Error caught by boundary:', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">⚠️ Error</h1>
            <p className="text-slate-700 mb-4">{this.state.error?.message}</p>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<'home' | 'login' | 'dashboard'>('home')

  const handleLogin = (authData: AuthResponse) => {
    setUser(authData.user)
    setToken(authData.access_token)
    setCurrentView('dashboard')
    localStorage.setItem('token', authData.access_token)
    localStorage.setItem('user', JSON.stringify(authData.user))
  }

  const handleQuickLogin = (role: string) => {
    const userMap = {
      investor_micro: {
        id: '01',
        email: 'rina@aco.com',
        name: 'Rina Wijaya',
        role: 'investor_micro',
        isVerified: true,
        kycStatus: 'verified'
      },
      investor_enterprise: {
        id: '02',
        email: 'budi@aco.com',
        name: 'Budi Santoso',
        role: 'investor_enterprise',
        isVerified: true,
        kycStatus: 'verified'
      },
      project_owner: {
        id: '03',
        email: 'dimas@aco.com',
        name: 'Dimas Pratama',
        role: 'project_owner',
        isVerified: true,
        kycStatus: 'verified'
      },
      muzakki: {
        id: '04',
        email: 'salim@aco.com',
        name: 'Pak Salim Hartono',
        role: 'muzakki',
        isVerified: true,
        kycStatus: 'verified'
      },
      munfiq_mutashadiq: {
        id: '05',
        email: 'tari@aco.com',
        name: 'Bu Tari Rahayu',
        role: 'munfiq_mutashadiq',
        isVerified: true,
        kycStatus: 'verified'
      },
      wakif: {
        id: '06',
        email: 'mahmud@aco.com',
        name: 'Haji Mahmud',
        role: 'wakif',
        isVerified: true,
        kycStatus: 'verified'
      },
      mustahiq: {
        id: '07',
        email: 'ruslan@aco.com',
        name: 'Pak Ruslan',
        role: 'mustahiq',
        isVerified: true,
        kycStatus: 'verified'
      },
      investment_officer: {
        id: '08',
        email: 'arief@aco.com',
        name: 'Arief Wijaksana',
        role: 'investment_officer',
        isVerified: true,
        kycStatus: 'verified'
      },
      portfolio_monitor: {
        id: '09',
        email: 'sinta@aco.com',
        name: 'Sinta Kusuma',
        role: 'portfolio_monitor',
        isVerified: true,
        kycStatus: 'verified'
      },
      finance_officer: {
        id: '10',
        email: 'hendra@aco.com',
        name: 'Hendra Gunawan',
        role: 'finance_officer',
        isVerified: true,
        kycStatus: 'verified'
      },
      admin: {
        id: '11',
        email: 'reza@aco.com',
        name: 'Reza Pratama',
        role: 'admin',
        isVerified: true,
        kycStatus: 'verified'
      }
    }

    const user = userMap[role as keyof typeof userMap]
    if (user) {
      handleLogin({
        access_token: `mock_jwt_token_${user.id}`,
        user
      })
    }
  }

  const handleLogout = () => {
    setUser(null)
    setToken(null)
    setCurrentView('home')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  return (
    <ErrorBoundary>
      <div className="App">
        {currentView === 'home' && (
          <HomePage 
            onLoginClick={() => setCurrentView('login')} 
            onQuickLoginClick={handleQuickLogin}
          />
        )}
        {currentView === 'login' && (
          <BeautifulLogin 
            onLogin={handleLogin} 
            onQuickLogin={handleQuickLogin} 
          />
        )}
        {currentView === 'dashboard' && user && (
          <UniversalDashboard 
            user={user} 
            onLogout={handleLogout} 
          />
        )}
      </div>
    </ErrorBoundary>
  )
}

export default App